/**
 * @file LogoutModal.tsx
 *
 * @description Confirmation dialog for signing out, plus the full-screen
 * "Signing you out..." overlay shown while the WorkOS sign-out is in flight.
 *
 * @module Modals
 */
import { useState } from "react";
import { Box, CircularProgress, Fade, Portal, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import LogoutIcon from "@mui/icons-material/Logout";
import { useToast } from "@/shared/toast";
import { ConfirmDialog } from "../index";

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

interface LogoutModalProps {
  open: boolean;
  onClose: () => void;
  // May return a promise; rejections (and synchronous throws) surface as the
  // error toast. The default WorkOS signOut navigates away and returns void.
  onLogout: () => void | Promise<void>;
}

const LogoutModal = ({ open, onClose, onLogout }: LogoutModalProps) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const toast = useToast();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    onClose();
    try {
      await onLogout();
    } catch (error) {
      setIsLoggingOut(false);
      console.error("Logout failed:", error);
      toast.error("Sign out failed. Please try again.");
    }
  };

  return (
    <>
      <Portal>
        <Fade in={isLoggingOut} timeout={300} unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 3,
              backdropFilter: "blur(8px)",
              backgroundColor: "background.default",
            }}
          >
            <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  border: (t) => `3px solid ${t.palette.divider}`,
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              />
              <CircularProgress
                size={48}
                sx={{ color: "primary.main", position: "absolute" }}
              />
            </Box>
            <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }}>
              Signing you out...
            </Typography>
          </Box>
        </Fade>
      </Portal>

      <ConfirmDialog
        open={open}
        onClose={onClose}
        onConfirm={handleLogout}
        tone="error"
        icon={<LogoutIcon />}
        title="Sign Out?"
        description="Are you sure you want to sign out? You'll need to log in again to access your account."
        confirmLabel="Sign Out"
      />
    </>
  );
};

export default LogoutModal;
